import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider, useLang } from "@/i18n";
import { LangGate } from "@/components/LangSwitcher";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Room from "@/pages/Room";
import GuestJoin from "@/pages/GuestJoin";
import MeetingDetail from "@/pages/MeetingDetail";
import JobPage from "@/pages/JobPage";
import OrgSettings from "@/pages/OrgSettings";
import Admin from "@/pages/Admin";
import Account from "@/pages/Account";
import Legal from "@/pages/Legal";
import Contacts from "@/pages/Contacts";
import { IncomingCall } from "@/components/IncomingCall";

function Protected({ children, role }) {
  const { user } = useAuth();
  const { t } = useLang();
  if (user === null) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-vox-muted" data-testid="auth-checking">
        <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" />{t("common.loading")}</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

function Anonymous({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Anonymous><Login /></Anonymous>} />
      <Route path="/register" element={<Anonymous><Register /></Anonymous>} />
      <Route path="/join/:id" element={<GuestJoin />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/room/:id" element={<Protected><Room /></Protected>} />
      <Route path="/meetings/:id" element={<Protected><MeetingDetail /></Protected>} />
      <Route path="/jobs/:id" element={<Protected><JobPage /></Protected>} />
      <Route path="/org" element={<Protected><OrgSettings /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
      <Route path="/legal/:doc" element={<Legal />} />
      <Route path="/admin" element={<Protected role="platform_admin"><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <LangGate />
            <AppRouter />
            <IncomingCall />
            <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#152B33", border: "1px solid #1F3B44", color: "#F4F4F4" } }} />
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}
